import { createUuid } from "../../../lib/trace/context";
import { manualBusDayRegions } from "./quoteBudgetBusRegionService";
import { chinaAreaRegions } from "../../../shared/region";
import { pointQuickEntryResourceEntryType } from "../../../shared/itinerary-resource-entry";
import { MANUAL_CREW_ITEMS } from "../config/quoteBudgetManualEntryConfig";
import type { QuoteBudgetEntryContext, QuoteBudgetLine, QuoteBudgetQuantityMode } from "../types";
import type { QuoteBudgetDrawerDraft } from "../types/localDraft";
import type { ManualEntryBuildOptions, ManualEntryContext, ManualEntryDraft, ManualEntryItem, ManualEntryParseResult, ManualEntrySource, ManualEntryStatus } from "../types/manualEntry";

const MAX_CENTS = 9_007_199_254_740_900n;
const ROLES = /^(学生|老师|导游|司机|司陪)/u;
const ROLE_KEYS = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
const RESOURCE_TYPES = { stay: "住宿", point: "考察点", meal: "餐", air: "大交通", ship: "大交通", bus: "包车", crew: "导游", misc: "杂费" } as const;
const normalize = (value: string) => value.normalize("NFKC").trim();
const amountText = (cents: bigint) => `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
const toCents = (value: string) => { const [whole, fraction = ""] = value.split("."); return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0")); };

class EntryFailure extends Error {
  constructor(readonly status: ManualEntryStatus, readonly code: string, message: string) { super(message); }
}

/** 区分缺失因素与非法整数；仅餐费实际人数允许0。 */
function requireFactor(value: string, label: string, allowZero = false): number {
  const text = value.trim();
  if (!text || /人数|次数|餐数|晚数|天数|辆数|数量|范围|天次/.test(text)) throw new EntryFailure("missing_factor", "factor_required", `请补充${label}`);
  if (!/^\d+$/.test(text) || !Number.isSafeInteger(Number(text)) || Number(text) < (allowZero ? 0 : 1)) throw new EntryFailure("invalid", "factor_invalid", `${label}必须为${allowZero ? "非负" : "正"}整数`);
  return Number(text);
}

/** 独立校验单价，避免联动零人数掩盖超限价格。 */
function readPrice(value: string): string | null {
  const text = value.replace(/元$/u, "").trim();
  if (!text || /^(单价|餐标|金额)$/.test(text)) return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new EntryFailure("invalid", "price_invalid", "价格必须为非负数字，最多两位小数");
  if (toCents(text) > MAX_CENTS) throw new EntryFailure("invalid", "price_overflow", "单价超出可安全计算范围");
  return amountText(toCents(text));
}

/** 切分非空记录，同时保留原始字符位置。 */
function segments(text: string, pattern: RegExp, offset = 0, whole = text): ManualEntrySource[] {
  return [...text.matchAll(pattern)].filter(match => match[0].trim()).map(match => {
    const start = offset + (match.index ?? 0) + match[0].search(/\S/u), value = match[0].trim();
    return { text: value, start, end: start + value.length, line: whole.slice(0, start).split("\n").length };
  });
}

/** 角色和车型分隔符之间的空片段必须参与校验。 */
function commaSegments(source: ManualEntrySource, whole: string): ManualEntrySource[] {
  let cursor = source.start;
  return source.text.split(/[,，]/u).map(part => {
    const start = cursor + Math.max(0, part.search(/\S/u)); cursor += part.length + 1;
    return { text: part.trim(), start, end: start + part.trim().length, line: whole.slice(0, start).split("\n").length };
  });
}

/** 只有表头四类角色具备统一人数。 */
function roleCount(role: string | null, context: ManualEntryContext): number {
  if (!role || !(role in ROLE_KEYS)) throw new EntryFailure("missing_factor", "people_required", "请填写本条实际人数");
  const count = context.people[ROLE_KEYS[role as keyof typeof ROLE_KEYS]] ?? 0;
  if (!Number.isSafeInteger(count) || count < 0) throw new EntryFailure("invalid", "people_invalid", "统一人数必须为非负整数");
  return count;
}

/** 拦截未替换的名称占位符与超长名称。 */
function requireName(name: string, limit = 60): string {
  if (!name || /^(SKU名称|费用名称|住宿标准|车型)$/.test(name)) throw new EntryFailure("missing_factor", "name_required", "请填写实际名称");
  if (name.length > limit || /[<>]/.test(name)) throw new EntryFailure("invalid", "name_invalid", `名称请控制在${limit}字以内且不含尖括号`);
  return name;
}

type DraftInput = { name: string; resourceEntryType?: string; role?: string | null; unit: string; mode: QuoteBudgetQuantityMode; count?: number | null; factor?: number; price: string | null; group: string; label: string; identity?: QuoteBudgetEntryContext; mealType?: string; spuId?: string; skuId?: string };

/** 使用整数分派生金额，并保存不可从零人数反推的周期。 */
function makeDraft(input: DraftInput, context: ManualEntryContext): ManualEntryDraft {
  const factor = input.factor ?? 1, role = input.role ?? null;
  const count = input.mode === "linked_people" ? roleCount(role, context) : input.mode === "total" ? 1 : input.count ?? 1;
  const quantity = BigInt(count) * BigInt(factor);
  if (quantity * 100n > MAX_CENTS) throw new EntryFailure("invalid", "quantity_overflow", "数量超出可安全计算范围");
  const cents = input.price === null ? 0n : quantity * toCents(input.price);
  if (cents > MAX_CENTS) throw new EntryFailure("invalid", "amount_overflow", "金额超出可安全计算范围");
  return {
    category: context.category, name: input.name, resourceEntryType: input.resourceEntryType, role, unit: input.unit, quantity: String(quantity),
    explicitPeopleCount: input.mode === "fixed_people" ? count : null, unitPriceText: input.price, amount: amountText(cents),
    mealType: input.mealType, spuId: input.spuId, skuId: input.skuId,
    entrySnapshot: { schemaVersion: 1, inputMode: "manual_v2", groupId: context.groupId ?? input.group, groupLabel: input.label, quantityMode: input.mode, billingCount: factor, context: input.identity ?? {} },
  };
}

/** 有周期的两因子联动，三因子固定人数。 */
function readPeopleFormula(text: string, factorLabel: string, allowZeroPeople = false) {
  const values = formulaParts(text);
  if (values.length !== 2 && values.length !== 3) throw new EntryFailure(values.length > 3 ? "invalid" : "missing_factor", "formula_incomplete", `请填写${factorLabel}×单价或人数×${factorLabel}×单价`);
  const unit = factorLabel === "次数" ? "次场" : factorLabel.slice(0, 1);
  validateUnits(text, values.length === 3 ? ["人", unit, "元"] : [unit, "元"]);
  const count = values.length === 3 ? requireFactor(values[0], "人数", allowZeroPeople) : null;
  return { count, factor: requireFactor(values.at(-2)!, factorLabel), price: readPrice(values.at(-1)!) };
}

/** 仅删除数字后的单位，保留“次数”等缺因素占位词。 */
function formulaParts(text: string): string[] {
  return normalize(text).replace(/\s/gu, "").split(/[xX×*]/u).map(value => value.replace(/(?<=\d)(?:人|次|场|晚|天|餐|辆|桌|元)$/u, ""));
}

/** 单位只能出现在对应因素位置，不能通过删除单位改变语义。 */
function validateUnits(text: string, units: string[]): void {
  normalize(text).replace(/\s/gu, "").split(/[xX×*]/u).forEach((part, index) => {
    const unit = part.match(/[人次场晚天餐辆桌元]$/u)?.[0];
    if (unit && !units[index]?.includes(unit)) throw new EntryFailure("invalid", "unit_invalid", "公式单位与当前因素不一致");
  });
}

/** 按桌：桌数×桌价，不乘用餐人数。 */
function readTableFormula(text: string) {
  const values = formulaParts(text);
  if (values.length !== 2) throw new EntryFailure(values.length > 2 ? "invalid" : "missing_factor", "table_formula", "请填写桌数×单价");
  validateUnits(text, ["桌", "元"]);
  return { count: requireFactor(values[0], "桌数"), price: readPrice(values[1]) };
}

/** 无周期人数公式：单价联动，人数乘单价固定。 */
function readPersonPrice(text: string) {
  const values = formulaParts(text);
  if (values.length > 2) throw new EntryFailure("invalid", "formula_invalid", "请填写单价或人数×单价");
  validateUnits(text, values.length === 2 ? ["人", "元"] : ["元"]);
  return { count: values.length === 2 ? requireFactor(values[0], "人数") : null, price: readPrice(values.at(-1)!) };
}

/** 匹配真实区域并保持单区域修改边界。 */
function requireArea(name: string | undefined, context: ManualEntryContext) {
  const matches = (context.areas ?? []).filter(area => name ? area.name === name || area.id === name : area.id === context.areaId);
  if (!matches.length) throw new EntryFailure(name ? "invalid" : "missing_factor", "area_required", name ? `没有匹配的行程区域“${name}”` : "请先选择住宿区域");
  if (matches.length > 1) throw new EntryFailure("invalid", "area_ambiguous", "区域名称不唯一，请选择准确区域");
  if (context.areaId && context.areaId !== matches[0].id) throw new EntryFailure("invalid", "area_scope", "本次修改只能录入当前区域");
  return matches[0];
}

/** 住宿每项独立声明角色、标准和实际晚数。 */
function parseStay(text: string, context: ManualEntryContext): ManualEntryItem[] {
  return segments(text, /[^\n;；]+/gu).map(source => checked(source, () => {
    const area = requireArea(undefined, context), value = normalize(source.text), role = value.match(ROLES)?.[1];
    if (!role || role === "司陪") throw new EntryFailure("missing_factor", "role_required", "请填写学生、老师、导游或司机");
    const match = value.slice(role.length).trim().match(/^(.+?)\s+([\d人数晚数][\s\S]*)$/u);
    if (!match) throw new EntryFailure("missing_factor", "stay_required", "请填写住宿标准和晚数×单价");
    const name = requireName(match[1]), formula = readPeopleFormula(match[2], "晚数");
    const roleCode = ({ 学生: "student", 老师: "teacher", 导游: "guide", 司机: "driver" } as Record<string, string>)[role];
    const standards = (area.standards ?? []).filter(item => item.name === name && (!item.role || item.role === role || item.role === roleCode)), standard = standards.length === 1 ? standards[0] : undefined;
    return [makeDraft({ name, role, unit: "人晚", mode: formula.count === null ? "linked_people" : "fixed_people", count: formula.count, factor: formula.factor, price: formula.price,
      group: `stay:${area.id}`, label: area.name, identity: { areaId: area.id, areaName: area.name }, spuId: standard?.spuId, skuId: standard?.skuId }, context)];
  }));
}

/** 餐跨片段继承上下文，遇新区域即清除旧角色与餐别；含「桌」时按桌数×单价，角色可省略。 */
function parseMeal(text: string, context: ManualEntryContext): ManualEntryItem[] {
  let areaName: string | undefined, role: string | null = null, mealType: string | undefined;
  const items = segments(text, /[^\n;；,，]+/gu).map(source => checked(source, () => {
    let rest = normalize(source.text);
    if (rest.startsWith("@")) {
      areaName = undefined; role = null; mealType = undefined;
      const match = rest.match(/^@(.+?)(?=\s|学生|老师|导游|司机|正餐|早餐|中餐|晚餐|$)/u);
      if (!match) throw new EntryFailure("missing_factor", "area_required", "请填写区域名称");
      areaName = match[1]; areaName = requireArea(areaName, context).name; rest = rest.slice(match[0].length).trim();
    }
    const area = requireArea(areaName, context), roleMatch = rest.match(ROLES);
    if (roleMatch) { role = roleMatch[1]; rest = rest.slice(role.length).trim(); }
    const mealMatch = rest.match(/^(正餐|早餐|中餐|晚餐)/u);
    if (mealMatch) { mealType = mealMatch[1]; rest = rest.slice(mealType.length).trim(); }
    if (!mealType) throw new EntryFailure("missing_factor", "meal_required", "请填写餐别");
    if (/桌/u.test(rest)) {
      if (role === "司陪") throw new EntryFailure("invalid", "role_invalid", "按桌计费不能使用司陪角色");
      const table = readTableFormula(rest);
      return [makeDraft({ name: mealType, role, unit: "桌", mode: "quantity", count: table.count, factor: 1, price: table.price,
        group: `meal:${area.id}`, label: area.name, identity: { areaId: area.id, areaName: area.name }, mealType }, context)];
    }
    if (!role || role === "司陪") throw new EntryFailure("missing_factor", "role_required", "请填写用餐角色");
    const formula = readPeopleFormula(rest, "餐数", true);
    if (role === "学生" && formula.count !== null && formula.count !== roleCount(role, context)) {
      throw new EntryFailure("invalid", "student_count_mismatch", `学生统一为${roleCount(role, context)}人，请省略人数或填写统一学生人数`);
    }
    const mode = role === "学生" || formula.count === null ? "linked_people" : "fixed_people";
    return [makeDraft({ name: mealType, role, unit: "人餐", mode, count: formula.count, factor: formula.factor, price: formula.price,
      group: `meal:${area.id}`, label: area.name, identity: { areaId: area.id, areaName: area.name }, mealType }, context)];
  }));
  const rows = items.flatMap(item => item.drafts);
  items.forEach(item => item.drafts.forEach(row => {
    const peers = rows.filter(other => other !== row && other.role === row.role && other.entrySnapshot.context?.areaId === row.entrySnapshot.context?.areaId);
    if (peers.some(other => (row.mealType === "正餐" && ["中餐", "晚餐"].includes(other.mealType ?? "")) || (other.mealType === "正餐" && ["中餐", "晚餐"].includes(row.mealType ?? "")))) item.warnings.push("同区域角色混用正餐和中/晚餐，请核对是否重复");
    if (peers.some(other => other.mealType === row.mealType && other.quantity === row.quantity && other.unitPriceText === row.unitPriceText)) item.warnings.push("存在相同用餐报价，请核对");
  }));
  return items;
}

/** 城市名称或简称只能唯一命中真实行政区候选。 */
function requireCity(name: string, context: ManualEntryContext) {
  const cities = context.cities ?? chinaAreaRegions.flatMap(province => province.cities);
  const matches = cities.filter(city => city.name === name || city.name.replace(/市$/u, "") === name || ("aliases" in city && (city.aliases as string[] | undefined)?.includes(name)));
  if (matches.length !== 1) throw new EntryFailure("invalid", "city_invalid", matches.length ? `城市“${name}”不唯一，请选择准确城市` : `没有匹配城市“${name}”`);
  return matches[0];
}

/** 逗号沿用路线，换行和分号重置；不派生手续费。 */
function parseCityTransport(text: string, context: ManualEntryContext): ManualEntryItem[] {
  return segments(text, /[^\n;；]+/gu).flatMap(record => {
    let route: QuoteBudgetEntryContext | undefined;
    return commaSegments(record, text).map(source => checked(source, () => {
      let rest = normalize(source.text);
      if (!ROLES.test(rest)) {
        route = undefined;
        const match = rest.match(/^(.+?)\s*[-—→]\s*(.+?)(?=\s*(?:学生|老师|导游|司机|司陪)|$)/u);
        if (!match || !match[1].trim() || !match[2].trim()) throw new EntryFailure("missing_factor", "route_required", "请填写起点城市-终点城市和角色");
        const from = requireCity(match[1].trim(), context), to = requireCity(match[2].trim(), context);
        route = { fromCityCode: from.code, fromCityName: from.name, toCityCode: to.code, toCityName: to.name };
        rest = rest.slice(match[0].length).trim();
      }
      if (!route) throw new EntryFailure("missing_factor", "route_required", "每条记录请先填写城市路线");
      const role = rest.match(ROLES)?.[1];
      if (!role || role === "司陪") throw new EntryFailure("missing_factor", "role_required", context.category === "ship" ? "请填写乘船角色" : "请填写乘机角色");
      if (role === "导游") throw new EntryFailure("invalid", "role_invalid", context.category === "ship" ? "船票不支持导游角色" : "机票不支持导游角色");
      const formula = readPersonPrice(rest.slice(role.length));
      const label = `${route.fromCityName}-${route.toCityName}`;
      return [makeDraft({ name: label, role, unit: "人", mode: formula.count === null ? "linked_people" : "fixed_people", count: formula.count, price: formula.price,
        group: `${context.category}:${route.fromCityCode}:${route.toCityCode}`, label, identity: route }, context)];
    }));
  });
}

/** 日期范围由真实行程约束，不硬编码演示天数。 */
function parseDayRange(value: string, context: ManualEntryContext, service?: string): number[] {
  const text = normalize(value).replace(/第|天/gu, "").trim();
  const available = context.itineraryDays?.map(day => day.day);
  const maxDay = context.dayCount ?? (available?.length ? Math.max(...available) : 0);
  let days: number[];
  if (/^\d+\s*[-~至]\s*\d+$/u.test(text)) {
    const [start, end] = text.split(/[-~至]/u).map(part => requireFactor(part, "天次"));
    const confirmedMax = Math.max(0, ...(context.confirmedBusScopes ?? []).flatMap(scope => scope.dayNumbers ?? []));
    if (start >= end || end > Math.max(maxDay, confirmedMax)) throw new EntryFailure("invalid", "range_invalid", "连续日期范围必须递增且在真实行程内");
    days = Array.from({ length: end - start + 1 }, (_, index) => start + index);
  } else days = text.split(/[,，、]/u).map(part => requireFactor(part, "天次"));
  const serviceType = service === "单接" ? "pickup" : service === "单送" ? "dropoff" : "local_charter";
  if (context.confirmedBusScopes?.some(scope => scope.serviceType === serviceType
    && scope.dayNumbers?.join(",") === days.join(",") && scope.regionKey && scope.regionName)) return days;
  if (!maxDay) throw new EntryFailure("missing_factor", "days_required", "请先完善真实行程天次");
  if (days.some((day, index) => day > maxDay || (available?.length && !available.includes(day)) || (index > 0 && day <= days[index - 1]))) throw new EntryFailure("invalid", "range_invalid", "日期必须在行程内升序排列且不重复");
  return days;
}

/** 当地用车服务文案；输入仍接受「包车」别名。 */
function busLocalServiceLabel(): string {
  return "当地用车";
}

/** 多车型共享用车组，成对接送展开为两个独立组。 */
function parseBus(text: string, context: ManualEntryContext): ManualEntryItem[] {
  const batchId = createUuid();
  const records = segments(text, /[^\n;；]+/gu);
  return records.flatMap(record => {
    // 连续区间（含 -/~ /至）未写服务时默认当地用车，无需再填「包车/当地用车」。
    let header = record.text.match(/^(.+?)\s+(包车|当地用车|单接|单送|接送)\s+([\s\S]*)$/u);
    if (!header) {
      const bare = record.text.match(/^(\d+\s*[-~至]\s*\d+)\s+([\s\S]+)$/u);
      if (bare) header = Object.assign([`${bare[1]} ${busLocalServiceLabel()} ${bare[2]}`, bare[1], busLocalServiceLabel(), bare[2]], { index: 0, input: record.text, groups: undefined }) as RegExpMatchArray;
    }
    const setup = checked(record, () => {
      if (!header) throw new EntryFailure("missing_factor", "bus_required", "请填写范围、服务、车型和辆数×整段单车价");
      const days = parseDayRange(header[1], context, header[2]), service = header[2];
      if ((service === "单接" || service === "单送") && days.length !== 1) throw new EntryFailure("invalid", "transfer_days", "单接、单送每笔只能填写一天");
      if (service === "接送" && (days.length !== 2 || !/[,，、]/u.test(header[1]) || /[-~至]/u.test(header[1]))) throw new EntryFailure("invalid", "transfer_pair", "成对接送请填写两个升序离散日");
      return [];
    });
    if (setup.status === "invalid" || setup.status === "missing_factor" || !header) return [setup];
    const days = parseDayRange(header[1], context, header[2]), service = header[2];
    const groups = service === "接送" ? [{ days: [days[0]], service: "dropoff" as const }, { days: [days[1]], service: "pickup" as const }] : [{ days, service: service === "单接" ? "pickup" as const : service === "单送" ? "dropoff" as const : "local_charter" as const }];
    const bodyOffset = record.text.lastIndexOf(header[3]);
    const vehicleParts = header[3].split(/[,，、]/u);
    let cursor = Math.max(0, bodyOffset);
    const sources = vehicleParts.map(part => { const start = record.start + cursor + Math.max(0, part.search(/\S/u)); cursor += part.length + 1; return { text: part.trim(), start, end: start + part.trim().length, line: record.line }; });
    return groups.flatMap(group => sources.map(source => {
      const item = checked(source, () => {
        const match = normalize(source.text).match(/^(.+?)\s+([\s\S]+)$/u);
        if (!match) throw new EntryFailure(source.text ? "missing_factor" : "invalid", "vehicle_required", "请填写车型和辆数×整段单车价");
        const name = requireName(match[1]), candidates = (context.vehicleTypes ?? []).filter(type => type.name === name);
        if (candidates.length !== 1) throw new EntryFailure("invalid", "vehicle_invalid", `车型“${name}”未唯一命中真实车型候选`);
        const parts = formulaParts(match[2]);
        if (parts.length !== 2) throw new EntryFailure("missing_factor", "vehicle_formula", "请填写辆数×整段单车价");
        validateUnits(match[2], ["辆", "元"]);
        const count = requireFactor(parts[0], "辆数"), price = readPrice(parts[1]), vehicle = candidates[0];
        // 同批内相同天次+服务共享用车组（逗号同行或换行/分号分行多车型均合并），不按原文偏移拆组。
        const rangeKey = `${group.service}:${group.days.join(",")}`;
        const groupId = context.groupId
          ? groups.length > 1 || records.length > 1 ? `${context.groupId}:${rangeKey}` : context.groupId
          : `bus:${batchId}:${rangeKey}`;
        const dayRegions = group.days.map(day => manualBusDayRegions(
          context.itineraryDays?.find(item => item.day === day),
        ));
        const places = dayRegions.flat();
        const completeRegion = places.every(place => place?.cityName && place.provinceName);
        const confirmed = context.confirmedBusScopes?.filter(scope => scope.serviceType === group.service
          && scope.dayNumbers?.join(",") === group.days.join(",") && scope.regionKey && scope.regionName) ?? [];
        const confirmedRegions = new Set(confirmed.map(scope => `${scope.regionKey}|${scope.regionName}`));
        if (confirmedRegions.size > 1) throw new EntryFailure("invalid", "bus_scope_conflict", "原用车组城市范围冲突，请分组核对后修改");
        if (!confirmed.length && !completeRegion) {
          const missing = group.days.filter((_, index) => dayRegions[index].some(
            place => !place.cityName || !place.provinceName,
          ));
          throw new EntryFailure("missing_factor", "bus_region_required", `第${missing.join("、")}天缺少完整省市依据，请先在行程编辑中补齐，再修改本组确认城市范围`);
        }
        const region = confirmed.length ? { regionKey: confirmed[0].regionKey, regionName: confirmed[0].regionName } : completeRegion ? {
          regionKey: [...new Set(places.map(place => `${place!.provinceName}/${place!.cityName}`))].join("—"),
          regionName: [...new Set(places.map(place => place!.cityName))].join("—"),
        } : {};
        return [makeDraft({ name, unit: "辆", mode: "quantity", count, price, group: groupId,
          label: `${group.days.join(",")} ${group.service === "pickup" ? "单接" : group.service === "dropoff" ? "单送" : busLocalServiceLabel()}`,
          identity: { vehicleTypeId: vehicle.id, vehicleSpec: vehicle.name, serviceType: group.service, dayNumbers: group.days, ...region },
        }, { ...context, groupId })];
      });
      if (group.days.length > 1 && context.itineraryDays?.some(day => group.days.includes(day.day) && /飞机|火车/u.test(day.transfer ?? ""))) item.warnings.push("连续用车范围包含火车/飞机转场，请核对");
      return item;
    }));
  });
}

/** 项目不跨行继承，角色可由分类规则继续推断。 */
function splitNamedFormula(source: ManualEntrySource) {
  const match = normalize(source.text).match(/^(\S+)\s+([\s\S]*)$/u);
  if (!match) throw new EntryFailure("missing_factor", "name_formula_required", "请填写费用名称和公式");
  const name = requireName(match[1]);
  let rest = match[2].trim();
  const role = rest.match(ROLES)?.[1] ?? null;
  if (role) rest = rest.slice(role.length).trim();
  return { name, role, rest };
}

/** 司导角色按快捷项目锁定，司陪要求真实固定人数。 */
function parseCrew(text: string, context: ManualEntryContext): ManualEntryItem[] {
  return segments(text, /[^\n;；]+/gu).map(source => checked(source, () => {
    const parsed = splitNamedFormula(source), config = MANUAL_CREW_ITEMS.find(item => item.name === parsed.name);
    const role = parsed.role ?? config?.role ?? null;
    if (!role) throw new EntryFailure("missing_factor", "role_required", "请填写司导费用角色");
    if (config && parsed.role && parsed.role !== config.role) throw new EntryFailure("invalid", "role_conflict", `${parsed.name}只能使用${config.role}角色`);
    const unit = config?.unit ?? (parsed.rest.includes("晚") ? "晚" : parsed.rest.includes("次") ? "次" : parsed.rest.includes("天") ? "天" : "人");
    if (/[天晚次]/u.test(parsed.rest.replace(/天数|晚数|次数/gu, "")) && !parsed.rest.includes(unit)) throw new EntryFailure("invalid", "unit_invalid", "公式单位与项目不一致");
    const formula = unit === "人" ? { ...readPersonPrice(parsed.rest), factor: 1 } : readPeopleFormula(parsed.rest, unit === "晚" ? "晚数" : unit === "次" ? "次数" : "天数");
    if (role === "司陪" && formula.count === null) throw new EntryFailure("missing_factor", "people_required", "司陪请填写本条实际人数");
    return [makeDraft({ name: parsed.name, role, unit: unit === "人" ? "人" : `人${unit}`, mode: formula.count === null ? "linked_people" : "fixed_people", count: formula.count, factor: formula.factor, price: formula.price,
      group: "crew", label: "司导费用" }, context)];
  }));
}

/** 杂费保留整项、联动人数、固定人数和人天四种方式；组身份按计费方式，供主表分组修改。 */
function parseMisc(text: string, context: ManualEntryContext): ManualEntryItem[] {
  return segments(text, /[^\n;；]+/gu).map(source => checked(source, () => {
    const { name, role, rest } = splitNamedFormula(source);
    if (name === "营业税") throw new EntryFailure("invalid", "system_fee", "营业税由系统计算，不可重复录入");
    if (/项/u.test(rest)) {
      const values = formulaParts(rest);
      if (values.length !== 2) throw new EntryFailure("missing_factor", "quantity_required", "请填写项数×单价");
      validateUnits(rest, ["项", "元"]);
      return [makeDraft({ name, role, unit: "项", mode: "quantity", count: requireFactor(values[0].replace(/项$/u, ""), "项数"),
        price: readPrice(values[1]), group: "misc:quantity", label: "数量 × 单价" }, context)];
    }
    const parts = formulaParts(rest), withDays = /天/u.test(rest) || parts.length === 3;
    if (withDays) {
      const formula = readPeopleFormula(rest, "天数");
      if (!role && formula.count === null) throw new EntryFailure("missing_factor", "people_required", "请填写角色或实际人数");
      return [makeDraft({ name, role, unit: "人天", mode: formula.count === null ? "linked_people" : "fixed_people", count: formula.count, factor: formula.factor, price: formula.price, group: "misc:people_days", label: "人数 × 天数" }, context)];
    }
    const formula = readPersonPrice(rest), mode = formula.count !== null ? "fixed_people" : role ? "linked_people" : "total";
    const group = mode === "total" ? "misc:total" : mode === "linked_people" ? "misc:linked" : "misc:fixed";
    const label = mode === "total" ? "整项金额" : mode === "linked_people" ? "联动人数" : "实际人数";
    return [makeDraft({ name, role, unit: mode === "total" ? "项" : "人", mode, count: formula.count, price: formula.price, group, label }, context)];
  }));
}

/** 把业务校验失败转换为预览状态，不吞掉程序异常。 */
function checked(source: ManualEntrySource, action: () => ManualEntryDraft[]): ManualEntryItem {
  try {
    const drafts = action(), missing = drafts.some(row => row.unitPriceText === null);
    return { source, status: missing ? "missing_price" : "valid", drafts,
      issues: missing ? [{ code: "price_required", message: "待补价；免费请明确填写0" }] : [],
      warnings: drafts.some(row => row.entrySnapshot.quantityMode === "linked_people" && row.quantity === "0") ? ["对应统一人数为0，本项金额为0"] : [],
    };
  } catch (error) {
    if (!(error instanceof EntryFailure)) throw error;
    return { source, status: error.status, issues: [{ code: error.code, message: error.message }], warnings: [], drafts: [] };
  }
}

/** 门票逗号只继承 SKU，每个角色片段重新解释人数模式。 */
function parsePoint(text: string, context: ManualEntryContext): ManualEntryItem[] {
  return segments(text, /[^\n;；]+/gu).flatMap(record => {
    const parts = commaSegments(record, text);
    let name = "";
    return parts.map((source, index) => {
      let rest = normalize(source.text), role: string | null = null;
      const item = checked(source, () => {
        const newSku = rest.startsWith("#");
        if (newSku) {
          name = "";
          const match = rest.match(/^#(.+?)\s+(.+)$/u);
          if (!match) throw new EntryFailure("missing_factor", "sku_required", "请填写#SKU名称和费用公式");
          name = requireName(match[1].trim(), 40); rest = match[2].trim();
        } else if (index === 0 || !name) throw new EntryFailure("missing_factor", "sku_required", "每条记录请先填写#SKU名称");
        const point = context.point;
        if (!point) throw new EntryFailure("missing_factor", "point_required", "请先选择行程考察点");
        role = rest.match(ROLES)?.[1] ?? null;
        if (role === "司陪") throw new EntryFailure("invalid", "role_invalid", "门票角色仅支持学生、老师、导游、司机");
        if (index > 0 && !newSku && !role) throw new EntryFailure("missing_factor", "role_required", "同SKU的逗号片段请填写角色");
        if (role) rest = rest.slice(role.length).trim();
        const formula = readPeopleFormula(rest, "次数");
        if (!role && formula.count !== null) throw new EntryFailure("invalid", "session_formula_invalid", "按场次仅填写次数×每场单价");
        const code = ({ 学生: "student", 老师: "teacher", 导游: "guide", 司机: "driver" } as Record<string, string>)[role ?? ""];
        const matches = (point.items ?? []).filter(item => item.skuName === name && (!item.travelerRole || item.travelerRole === role || item.travelerRole === code));
        if (matches.length > 1) throw new EntryFailure("invalid", "sku_ambiguous", "同名SKU存在多个候选，请选择准确项目");
        const matched = matches[0];
        return [makeDraft({ name, resourceEntryType: matched?.ticketResourceType ? pointQuickEntryResourceEntryType({ ticketResourceType: matched.ticketResourceType, skuName: matched.skuName }) : undefined, role, unit: role ? "人次" : "场", mode: role ? formula.count === null ? "linked_people" : "fixed_people" : "quantity",
          count: role ? formula.count : formula.factor, factor: role ? formula.factor : 1, price: formula.price,
          group: `point:${point.id}`, label: point.name, identity: { pointId: point.id, pointName: point.name }, spuId: point.id, skuId: matches[0]?.skuId,
        }, context)];
      });
      if (!item.issues.length) return item;
      const fixedPeople = formulaParts(rest).length === 3;
      const peopleGuide = `按人数填写：#费用名称 角色 ${fixedPeople ? "人数×" : ""}次数×单价，例如 #${name || "门票"} ${role && role !== "司陪" ? role : "学生"}${fixedPeople ? "20×" : ""}2×80`;
      const sessionGuide = `按场次填写：#费用名称 场次数×每场单价，例如 #${name || "讲解服务"} 2×800（不乘人数）`;
      const peopleFormula = Boolean(role) || fixedPeople;
      const guide = `${!role && fixedPeople ? "按人数计费请补充角色；" : ""}${peopleFormula ? peopleGuide : sessionGuide}`;
      // 保留原校验状态、错误码及原文定位，只在门票边界补足具体改法。
      item.issues = item.issues.map(issue => {
        let message: string;
        switch (issue.code) {
          case "sku_required": message = `请在#后填写费用名称，并补全费用公式；${peopleGuide}；${sessionGuide}`; break;
          case "name_required": message = `请填写实际费用名称，替换“SKU名称”或“费用名称”；${peopleGuide}；${sessionGuide}`; break;
          case "name_invalid": message = `费用${issue.message}；${peopleGuide}；${sessionGuide}`; break;
          case "role_required": case "role_invalid": message = `${issue.message}；${peopleGuide}；按场次请另起一项并省略角色：${sessionGuide}`; break;
          case "session_formula_invalid": message = `按人数计费请补充角色；${peopleGuide}；按场次仅保留场次数和每场单价：${sessionGuide}`; break;
          case "factor_required": case "factor_invalid": message = `${peopleFormula ? issue.message : issue.message.replace("次数", "场次数")}；${guide}`; break;
          case "formula_incomplete": message = `${role ? "请按人数计费补全次数、单价及需要固定的人数" : "请按场次计费填写场次数和每场单价"}；${guide}`; break;
          case "unit_invalid": message = `${issue.message}；${guide}`; break;
          case "price_invalid": message = `${peopleFormula ? "单价" : "每场单价"}必须为非负数字，最多两位小数；${guide}`; break;
          case "price_required": message = `待补价，请补充${peopleFormula ? "单价" : "每场单价"}；${guide}；免费请明确填写0`; break;
          default: return issue;
        }
        return { ...issue, message };
      });
      return item;
    });
  });
}

/** 合计校验覆盖追加或替换后的整个预算，错误时保留有效预览。 */
function finish(items: ManualEntryItem[], context: ManualEntryContext): ManualEntryParseResult {
  const validRows = items.flatMap(item => item.drafts), total = validRows.reduce((sum, row) => sum + toCents(row.amount), 0n);
  if (context.category === "bus") {
    const signatures = (context.remainingLines ?? []).filter(line => line.entrySnapshot?.inputMode === "manual_v2" && line.entrySnapshot.context?.vehicleTypeId).map(line => JSON.stringify([line.entrySnapshot?.context, String(line.quantity), line.priceSourceType === "unpriced" ? null : String(line.budgetUnitPrice)]));
    for (const item of items) for (const row of item.drafts) {
      const signature = JSON.stringify([row.entrySnapshot.context, row.quantity, row.unitPriceText]);
      if (signatures.includes(signature)) item.warnings.push("存在完全重复的用车报价，请核对");
      signatures.push(signature);
    }
  }
  const remaining = (context.remainingLines ?? []).reduce((sum, line) => {
    const value = String(line.budgetAmount); return sum + (/^\d+(?:\.\d{1,2})?$/.test(value) ? toCents(value) : MAX_CENTS + 1n);
  }, 0n);
  if ((total > MAX_CENTS || total + remaining > MAX_CENTS) && items.length) {
    items[items.length - 1] = { ...items.at(-1)!, status: "invalid", issues: [{ code: "total_overflow", message: "确认后的合计超出可安全计算范围" }] };
  }
  return { category: context.category, items, validRows, knownTotalAmount: amountText(total), canConfirm: items.length > 0 && items.every(item => item.status === "valid" || item.status === "missing_price") };
}

/** 解析新版人工文本并逐项返回校验结果。 */
export function parseManualEntries(text: string, context: ManualEntryContext): ManualEntryParseResult {
  const parsers = { stay: parseStay, point: parsePoint, meal: parseMeal, air: parseCityTransport, ship: parseCityTransport, bus: parseBus, crew: parseCrew, misc: parseMisc };
  const result = finish(parsers[context.category](text, context), context);
  const sources = context.editingLines ?? [];
  if (!result.canConfirm || !sources.length) return result;
  // 旧大巴多行转换由运行态的明确 replacesLineIds 处理，不伪装成其中任意一条原项目。
  if (context.category === "bus" && sources.every(line => !line.entrySnapshot)) return result;
  const parsedLines = buildManualDraftLines(result);
  const originalTexts = sources.map(line => {
    const originalText = formatManualEntries([line], context);
    const canonical = parseManualEntries(originalText, { ...context, editingLines: undefined });
    return canonical.canConfirm ? formatManualEntries(buildManualDraftLines(canonical), context) : originalText;
  });
  const parsedTexts = parsedLines.map(line => formatManualEntries([line], context));
  const used = new Set<string>();
  const sourceGroup = (line: QuoteBudgetLine) => context.category === "stay" || context.category === "meal"
    ? line.entrySnapshot?.context?.areaId ?? line.spuId ?? line.entrySnapshot?.groupId ?? line.budgetLineId
    : context.category === "point"
      ? line.entrySnapshot?.context?.pointId ?? line.spuId ?? line.entrySnapshot?.groupId ?? line.budgetLineId
      : line.entrySnapshot?.groupId ?? line.budgetLineId;
  const singleGroup = new Set(sources.map(sourceGroup)).size === 1;
  let ambiguous = false;
  const roleOf = (role: string | null | undefined) => ({ student: "学生", teacher: "老师", guide: "导游", driver: "司机" })[role ?? ""] ?? role ?? null;
  const slotOf = (line: QuoteBudgetLine) => context.category === "bus"
    ? line.entrySnapshot?.context?.vehicleTypeId ?? roleOf(line.travelerRole)
    : roleOf(line.travelerRole);
  result.validRows.forEach((row, index) => {
    // 只有完整回填内容唯一且未修改时，批量重排才能证明原项目来源。
    const exact = sources.filter((line, sourceIndex) => originalTexts[sourceIndex] !== ""
      && originalTexts[sourceIndex] === parsedTexts[index]);
    const sameRole = sources.filter(line => slotOf(line) === slotOf(parsedLines[index]));
    const parsedRoleCount = parsedLines.filter(other => slotOf(other) === slotOf(parsedLines[index])).length;
    const source = exact.length === 1 ? exact[0]
      : singleGroup && sameRole.length === 1 && parsedRoleCount === 1 ? sameRole[0] : undefined;
    if (source && !used.has(source.budgetLineId)) {
      row.sourceBudgetLineId = source.budgetLineId;
      used.add(source.budgetLineId);
    } else if (sameRole.length) {
      ambiguous = true;
    }
    // 原范围内不存在的角色明确视为新增；被取消的角色随替换范围删除。
  });
  if (ambiguous) {
    result.canConfirm = false;
    result.items[0].status = "invalid";
    result.items[0].issues.push({ code: "ambiguous_edit_identity", message: "无法唯一对应原项目，请保留本次输入并使用“修改项目”逐项修改；新增和删除请分别操作。" });
  }
  return result;
}
/** 仅允许整批校验成功后构建明细。 */
export function buildManualDraftLines(result: ManualEntryParseResult, options: ManualEntryBuildOptions = {}): QuoteBudgetLine[] {
  if (!result.canConfirm) return [];
  return result.validRows.map((row, index) => ({
    budgetLineId: row.sourceBudgetLineId ?? (options.createId ?? createUuid)(), lineNo: (options.firstLineNo ?? 1) + index,
    resourceType: RESOURCE_TYPES[row.category], resourceEntryType: row.resourceEntryType ?? (row.category === "air" ? "飞机" : row.category === "ship" ? "船" : row.category === "stay" ? "住宿" : row.category === "bus" ? row.entrySnapshot.context?.serviceType === "pickup" ? "单接" : row.entrySnapshot.context?.serviceType === "dropoff" ? "单送" : "包车" : row.category === "point" ? row.unit === "场" ? "讲解服务" : "门票" : row.name),
    itemNameSnapshot: row.name, itemSpecSnapshot: row.name, travelerRole: row.role, mealType: row.mealType,
    unit: row.unit, quantity: row.quantity, budgetUnitPrice: row.unitPriceText ?? "0.00", budgetAmount: row.amount,
    priceSourceType: row.unitPriceText === null ? "unpriced" : "manual", priceBasisDesc: "新版人工录入",
    peopleMode: row.entrySnapshot.quantityMode === "linked_people" ? "linked" : "manual", manualPeopleCount: row.explicitPeopleCount === null ? null : String(row.explicitPeopleCount),
    isLinkedToPeopleCount: row.entrySnapshot.quantityMode === "linked_people", isCapitaShared: true, isAgentCollection: row.category === "air" || row.category === "ship",
    nightCount: row.unit === "人晚" ? row.entrySnapshot.billingCount : null, dayCount: row.unit === "人天" ? row.entrySnapshot.billingCount : null,
    spuTableName: row.category === "point" ? "resource_point_spu" : row.category === "stay" ? "resource_stay_budget_spu" : null,
    spuId: row.spuId ?? null, spuNameSnapshot: row.entrySnapshot.groupLabel, skuTableName: row.category === "point" ? "resource_point_sku" : row.category === "stay" ? "resource_stay_budget_sku" : null,
    skuId: row.skuId ?? null, skuNameSnapshot: row.name, entrySnapshot: row.entrySnapshot,
  }));
}
/** 识别误写入输入框的主表展示公式，便于修改本组时改回填写示例口径。 */
export function looksLikeManualDisplayDump(text: string): boolean {
  return /(?:\d+人\s*[xX×*]\s*\d+|元\s*·\s*¥|·\s*¥[\d,]+)/u.test(text);
}

/** 单价回填：缺价保留“单价”，已有数字按原值写出，对齐填写示例而非展示公式。 */
function legacyPriceText(line: QuoteBudgetLine): string {
  if (line.resourceType === "杂费" && line.priceSourceType === "unpriced") return "单价";
  // 有数值单价（含 0）原样回填，与快捷模板零价占位一致；仅真正缺价才写「单价」待补。
  const raw = line.budgetUnitPrice == null ? "" : String(line.budgetUnitPrice).trim();
  if (raw !== "") return raw;
  return "单价";
}

/** 人数/辆数/天数等回填必须是正整数字符串；库内 DECIMAL 常把 50 存成 50.00。 */
function formatIntegerFactor(value: string | number | null | undefined): string | null {
  if (value == null || value === "") return null;
  const num = Number(String(value).trim());
  if (!Number.isFinite(num) || num <= 0) return null;
  const whole = Math.round(num);
  if (Math.abs(num - whole) > 1e-9 || !Number.isSafeInteger(whole)) return null;
  return String(whole);
}

/** 从行名提取 D 天次范围，供自动大巴回填人工语法。 */
function legacyBusDayNumbers(line: QuoteBudgetLine): number[] | null {
  const matched = String(line.itemNameSnapshot ?? "").match(/^D(\d+)(?:-D(\d+))?/u);
  if (!matched) return null;
  const start = Number(matched[1]);
  const end = Number(matched[2] ?? matched[1]);
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 1 || end < start) return null;
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

/** 送/接优先取燃油段名；无标记时默认当地用车。 */
function legacyBusServiceLabel(lines: QuoteBudgetLine[]): string {
  const text = lines.map((line) => `${line.itemNameSnapshot ?? ""} ${line.spuNameSnapshot ?? ""}`).join(" ");
  if (/单接|(?:^|[\s\u3000])接[:：]/u.test(text) || /D\d+(?:-D\d+)?\s*接(?:[:：\s]|$)/u.test(text)) return "单接";
  if (/单送|(?:^|[\s\u3000])送[:：]/u.test(text) || /D\d+(?:-D\d+)?\s*送(?:[:：\s]|$)/u.test(text)) return "单送";
  return busLocalServiceLabel();
}

/** 固定费依据中的座位数×日单价×辆数；忽略“用车天数=”说明段。 */
function legacyBusFixedVehicles(line: QuoteBudgetLine): Array<{ seat: number; count: number; dailyRate: number }> {
  const basis = String(line.priceBasisDesc ?? "").replace(/^按车型每日用车固定费：/u, "");
  const vehicles: Array<{ seat: number; count: number; dailyRate: number }> = [];
  for (const match of basis.matchAll(/(\d+)座(?:按\d+座)?(?:\s+\d+月)?\s+(\d+(?:\.\d+)?)元\/天\s*x\s*(\d+)辆/gu)) {
    const seat = Number(match[1]);
    const dailyRate = Number(match[2]);
    const count = Number(match[3]);
    if (Number.isSafeInteger(seat) && seat > 0 && Number.isSafeInteger(count) && count > 0
      && Number.isFinite(dailyRate) && dailyRate >= 0) {
      vehicles.push({ seat, count, dailyRate });
    }
  }
  return vehicles;
}

/** 规格中的车队；仅燃油回退时使用，整段单车价按金额÷总辆数。 */
function legacyBusFleetFromSpec(line: QuoteBudgetLine): Array<{ seat: number; count: number }> {
  const sources = [String(line.itemSpecSnapshot ?? ""), String(line.priceBasisDesc ?? "")];
  for (const source of sources) {
    const fleet = Array.from(source.matchAll(/(\d+)座\s*x\s*(\d+)辆/gu))
      .map((match) => ({ seat: Number(match[1]), count: Number(match[2]) }))
      .filter((item) => Number.isSafeInteger(item.seat) && item.seat > 0 && Number.isSafeInteger(item.count) && item.count > 0);
    if (fleet.length) return fleet;
  }
  return [];
}

/** 天次写成连续区间或逗号枚举，与新版大巴回填一致。 */
function legacyBusRangeText(days: number[]): string {
  return days.length > 1 && days.every((day, index) => index === 0 || day === days[index - 1] + 1)
    ? `${days[0]}-${days.at(-1)}` : days.join(",");
}

/** 固定费段关联同行燃油：天次相交优先；无天次时退回同 priceSourceRef。 */
function legacyBusRelatedLines(
  busLines: QuoteBudgetLine[],
  group: { days: number[]; lines: QuoteBudgetLine[] },
): QuoteBudgetLine[] {
  const daySet = new Set(group.days);
  const refs = new Set(
    group.lines.map((line) => line.priceSourceRef).filter((ref): ref is string => Boolean(ref)),
  );
  return busLines.filter((line) => {
    const lineDays = legacyBusDayNumbers(line);
    if (lineDays?.some((day) => daySet.has(day))) return true;
    return Boolean(!lineDays?.length && line.priceSourceRef && refs.has(line.priceSourceRef));
  });
}

/** 预算行金额转分；非法返回 null。 */
function legacyBusAmountCents(line: QuoteBudgetLine): bigint | null {
  const matched = String(line.budgetAmount ?? "").trim().match(/^(-?)(\d+)(?:\.(\d+))?$/u);
  if (matched) {
    return BigInt(matched[2]) * 100n + BigInt(`${matched[3] ?? ""}00`.slice(0, 2));
  }
  const quantity = Number(line.quantity);
  const unitPrice = Number(line.budgetUnitPrice);
  if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice) || quantity < 0 || unitPrice < 0) return null;
  const product = (quantity * unitPrice).toFixed(2);
  const fallback = product.match(/^(\d+)(?:\.(\d+))?$/u);
  return fallback ? BigInt(fallback[1]) * 100n + BigInt(`${fallback[2] ?? ""}00`.slice(0, 2)) : null;
}

/** 分按权重拆到各组，尾差落最后一组，合计不变。 */
function allocateCentsByWeights(totalCents: bigint, weights: number[]): bigint[] {
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  if (!weightSum || totalCents < 0n) return weights.map(() => 0n);
  let allocated = 0n;
  return weights.map((weight, index) => {
    if (index === weights.length - 1) return totalCents - allocated;
    const share = (totalCents * BigInt(weight)) / BigInt(weightSum);
    allocated += share;
    return share;
  });
}

/** 金额÷辆数得整段单车价，半入到分，与主表展示同口径。 */
function legacyBusSegmentPriceText(totalCents: bigint, vehicles: number): string | null {
  if (!Number.isSafeInteger(vehicles) || vehicles <= 0 || totalCents < 0n) return null;
  const divisor = BigInt(vehicles);
  const quotient = totalCents / divisor;
  const remainder = totalCents % divisor;
  const cents = quotient + (remainder * 2n >= divisor ? 1n : 0n);
  const whole = cents / 100n;
  const fraction = cents % 100n;
  return fraction === 0n ? String(whole) : `${whole}.${String(fraction).padStart(2, "0")}`;
}

/** 把自动/历史大巴组还原为人工语法；不同日单价的固定费分段分行，燃油仅作无固定费时的回退。 */
function formatLegacyBusManualEntries(lines: QuoteBudgetLine[]): string[] {
  const busLines = lines.filter((line) => ["包车", "大巴车"].includes(line.resourceType));
  if (!busLines.length) return [];
  const fixed = busLines.filter((line) => line.resourceEntryType === "包车固定费");
  const formatPrice = (value: number) => Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (fixed.length) {
    // 同燃油段内常因月费变化拆多段固定费，必须按各段天次分别回填，不能只取首段。
    // 批量修改跨多段时，送/接必须按各段关联燃油判定，不能用全部分类行拼一次标签。
    // 回填单价与主表「整段单车价」一致：本组固定费+应摊燃油的合计÷辆数。
    const groups = new Map<string, { days: number[]; lines: QuoteBudgetLine[] }>();
    for (const line of fixed) {
      const days = legacyBusDayNumbers(line);
      if (!days?.length) continue;
      const key = days.join(",");
      const group = groups.get(key) ?? { days, lines: [] };
      group.lines.push(line);
      groups.set(key, group);
    }
    const orderedGroups = [...groups.values()]
      .sort((left, right) => left.days[0] - right.days[0] || left.days.length - right.days.length);
    const fuelShares = new Map<string, bigint>();
    for (const fuel of busLines.filter((line) => line.resourceEntryType === "包车燃油费")) {
      const amount = legacyBusAmountCents(fuel);
      if (amount === null) continue;
      const sharing = orderedGroups.filter((group) => (
        legacyBusRelatedLines(busLines, group).some((line) => line.budgetLineId === fuel.budgetLineId)
      ));
      if (!sharing.length) continue;
      const shares = allocateCentsByWeights(amount, sharing.map((group) => group.days.length));
      sharing.forEach((group, index) => {
        const key = group.days.join(",");
        fuelShares.set(key, (fuelShares.get(key) ?? 0n) + shares[index]!);
      });
    }
    const records = orderedGroups.flatMap((group) => {
      const related = legacyBusRelatedLines(busLines, group);
      const service = legacyBusServiceLabel(related);
      const vehicles = new Map<number, { count: number; dailyRate: number }>();
      for (const line of group.lines) {
        for (const item of legacyBusFixedVehicles(line)) {
          const current = vehicles.get(item.seat);
          if (!current || item.count > current.count) vehicles.set(item.seat, { count: item.count, dailyRate: item.dailyRate });
        }
      }
      if (!vehicles.size) return [];
      const vehicleCount = [...vehicles.values()].reduce((sum, item) => sum + item.count, 0);
      let fixedCents = 0n;
      for (const line of group.lines) {
        const amount = legacyBusAmountCents(line);
        if (amount === null) {
          fixedCents = -1n;
          break;
        }
        fixedCents += amount;
      }
      const dayCount = group.days.length;
      const stackedCents = fixedCents < 0n
        ? null
        : fixedCents + (fuelShares.get(group.days.join(",")) ?? 0n);
      const segment = stackedCents === null
        ? null
        : legacyBusSegmentPriceText(stackedCents, vehicleCount);
      const parts = [...vehicles.entries()]
        .sort(([left], [right]) => right - left)
        .map(([seat, item]) => {
          const price = segment ?? formatPrice(item.dailyRate * dayCount);
          return `${seat}座 ${item.count}×${price}`;
        });
      return [`${legacyBusRangeText(group.days)} ${service} ${parts.join(",")}`];
    });
    if (records.length) return records;
  }
  const fuel = busLines.find((line) => line.resourceEntryType === "包车燃油费") ?? busLines[0];
  const days = legacyBusDayNumbers(fuel)
    ?? busLines.map(legacyBusDayNumbers).find((value): value is number[] => Boolean(value));
  if (!days?.length) return [];
  const service = legacyBusServiceLabel(legacyBusRelatedLines(busLines, { days, lines: [fuel] }));
  const fleet = legacyBusFleetFromSpec(fuel);
  const vehicleCount = fleet.reduce((sum, item) => sum + item.count, 0);
  const amountCents = legacyBusAmountCents(fuel);
  if (!fleet.length || vehicleCount < 1 || amountCents === null) return [];
  const segment = legacyBusSegmentPriceText(amountCents, vehicleCount);
  if (!segment) return [];
  const parts = fleet.map((item) => `${item.seat}座 ${item.count}×${segment}`);
  return [`${legacyBusRangeText(days)} ${service} ${parts.join(",")}`];
}

/** 把旧自动/历史明细转成填写示例同口径文本；无法安全还原时返回 null。 */
function formatLegacyManualEntry(
  line: QuoteBudgetLine,
  category: ManualEntryContext["category"],
  people?: ManualEntryContext["people"],
): string | null {
  if (category === "bus") return null;
  const name = (line.itemNameSnapshot || line.skuNameSnapshot || "").trim();
  const role = (line.travelerRole ?? "").trim();
  const price = legacyPriceText(line);
  if (category === "stay") {
    if (!role || !name || !/^(学生|老师|导游|司机)$/u.test(role)) return null;
    let nights = line.nightCount && Number.isSafeInteger(line.nightCount) && line.nightCount > 0 ? line.nightCount : null;
    if (!nights && people && line.isLinkedToPeopleCount) {
      const linked = roleCount(role, { category: "stay", people } as ManualEntryContext);
      if (linked > 0 && /^\d+$/u.test(line.quantity) && Number(line.quantity) % linked === 0) {
        nights = Number(line.quantity) / linked;
      }
    }
    if (!nights || !Number.isSafeInteger(nights) || nights < 1) return null;
    if (line.isLinkedToPeopleCount) return `${role} ${name} ${nights}×${price}`;
    const peopleText = formatIntegerFactor(line.manualPeopleCount)
      ?? (formatIntegerFactor(line.quantity) && Number(line.quantity) % nights === 0
        ? formatIntegerFactor(Number(line.quantity) / nights) : null);
    return peopleText ? `${role} ${name} ${peopleText}×${nights}×${price}` : null;
  }
  if (category === "misc") {
    if (!name) return null;
    if (line.unit === "项" && Number(line.quantity) !== 1) return `${name} ${line.quantity}项×${price}`;
    if (line.unit === "人天" || (line.dayCount && line.dayCount > 0)) {
      const days = line.dayCount && line.dayCount > 0 ? line.dayCount : null;
      if (!days) return null;
      if (line.isLinkedToPeopleCount && role) return `${name} ${role} ${days}天×${price}`;
      const count = formatIntegerFactor(line.manualPeopleCount);
      return count ? `${name}${role ? ` ${role}` : ""} ${count}人×${days}天×${price}` : null;
    }
    if (!role || line.unit === "项" || (!line.isLinkedToPeopleCount && !line.manualPeopleCount)) return `${name} ${price}`;
    if (line.isLinkedToPeopleCount) return `${name} ${role} ${price}`;
    const people = formatIntegerFactor(line.manualPeopleCount);
    if (people) return `${name} ${role} ${people}×${price}`;
    return `${name} ${role} ${price}`;
  }
  if (category === "crew") {
    if (!name || !role) return null;
    if (line.unit === "人" || (!line.dayCount && !line.nightCount && line.unit !== "人次" && line.unit !== "人晚" && line.unit !== "人天")) {
      if (line.isLinkedToPeopleCount) return `${name} ${role} ${price}`;
      const people = formatIntegerFactor(line.manualPeopleCount);
      return people ? `${name} ${role} ${people}×${price}` : null;
    }
    const factorUnit = line.unit === "人晚" ? "晚" : line.unit === "人次" ? "次" : "天";
    const factor = line.unit === "人晚" ? line.nightCount : line.unit === "人天" || factorUnit === "天" ? line.dayCount : null;
    const billing = factor && factor > 0 ? factor : null;
    if (!billing) return null;
    if (line.isLinkedToPeopleCount) return `${name} ${role} ${billing}${factorUnit}×${price}`;
    const people = formatIntegerFactor(line.manualPeopleCount);
    return people ? `${name} ${role} ${people}人×${billing}${factorUnit}×${price}` : null;
  }
  if (category === "point") {
    const sku = (line.skuNameSnapshot || line.itemNameSnapshot || "").trim();
    if (!sku) return null;
    const quantity = formatIntegerFactor(line.quantity);
    if (!quantity) return null;
    if (line.unit === "场") {
      return `#${sku} ${quantity}×${price}`;
    }
    if (!role || !/^(学生|老师|导游|司机)$/u.test(role)) return null;
    if (line.isLinkedToPeopleCount) {
      if (!people) return null;
      const linked = roleCount(role, { category: "point", people } as ManualEntryContext);
      if (linked < 1 || Number(line.quantity) % linked !== 0) return null;
      const times = Number(line.quantity) / linked;
      if (!Number.isSafeInteger(times) || times < 1) return null;
      return `#${sku} ${role} ${times}×${price}`;
    }
    const peopleFactor = formatIntegerFactor(line.manualPeopleCount);
    const count = peopleFactor ? Number(peopleFactor) : null;
    if (!count || Number(line.quantity) % count !== 0) return null;
    const times = Number(line.quantity) / count;
    if (!Number.isSafeInteger(times) || times < 1) return null;
    return `#${sku} ${role} ${count}×${times}×${price}`;
  }
  return null;
}

/** 当前回填与旧缓存识别共用序列化，仅天次表示方式不同。 */
function formatManualEntriesText(
  lines: QuoteBudgetLine[],
  context: Pick<ManualEntryContext, "category"> & Partial<Pick<ManualEntryContext, "people">>,
  expandedBusDays = false,
): string {
  const records: string[] = [], busGroups = new Map<string, { header: string; vehicles: string[] }>(), legacyBus: QuoteBudgetLine[] = [];
  for (const line of lines) {
    const snapshot = line.entrySnapshot;
    if (snapshot?.inputMode !== "manual_v2") {
      if (context.category === "bus") {
        legacyBus.push(line);
        continue;
      }
      const legacy = formatLegacyManualEntry(line, context.category, context.people);
      if (legacy) records.push(legacy);
      continue;
    }
    const identity = snapshot.context ?? {}, role = line.travelerRole ?? "";
    const name = context.category === "point"
      ? (line.skuNameSnapshot || line.itemNameSnapshot || "")
      : (line.itemNameSnapshot ?? line.skuNameSnapshot ?? "");
    const price = line.priceSourceType === "unpriced" ? "单价" : String(line.budgetUnitPrice);
    const peopleFactor = formatIntegerFactor(line.manualPeopleCount);
    const periodic = /^人[晚餐次天]$/u.test(line.unit);
    // 杂费/司导人数×天数（人晚/人次）回填带「人」，与填写示例「2人×3天×50」一致；住宿等仍写「人数×晚数×单价」。
    const peopleUnit = (context.category === "misc" || context.category === "crew") && periodic ? "人" : "";
    const count = snapshot.quantityMode === "fixed_people" && peopleFactor ? `${peopleFactor}${peopleUnit}×` : "";
    const billingFactor = formatIntegerFactor(snapshot.billingCount) ?? String(snapshot.billingCount ?? "");
    const formula = `${count}${periodic ? `${billingFactor}${context.category === "misc" || context.category === "crew" ? line.unit.slice(1) : ""}×` : ""}${price}`;
    if (context.category === "stay") records.push(`${role} ${name} ${formula}`);
    else if (context.category === "point") {
      const qtyFactor = formatIntegerFactor(line.quantity) ?? String(line.quantity ?? "");
      const body = snapshot.quantityMode === "quantity" ? `${qtyFactor}×${price}` : formula;
      records.push(`#${name} ${role ? `${role} ${body}` : body}`);
    }
    else if (context.category === "meal") {
      if (line.unit === "桌") {
        const qty = formatIntegerFactor(line.quantity) ?? String(line.quantity ?? "");
        records.push(`@${identity.areaName ?? snapshot.groupLabel}${role ? ` ${role}` : ""} ${line.mealType ?? name} ${qty}桌×${price}`);
      } else {
        records.push(`@${identity.areaName ?? snapshot.groupLabel} ${role} ${line.mealType ?? name}${formula}`);
      }
    }
    else if (context.category === "air" || context.category === "ship") records.push(`${identity.fromCityName}-${identity.toCityName} ${role}${formula}`);
    else if (context.category === "bus") {
      const days = identity.dayNumbers ?? [];
      const range = !expandedBusDays && days.length > 1 && days.every((day, index) => index === 0 || day === days[index - 1] + 1)
        ? `${days[0]}-${days.at(-1)}` : days.join(",");
      const group = busGroups.get(snapshot.groupId) ?? { header: `${range} ${identity.serviceType === "pickup" ? "单接" : identity.serviceType === "dropoff" ? "单送" : busLocalServiceLabel()}`, vehicles: [] };
      const vehicleQty = formatIntegerFactor(line.quantity) ?? String(line.quantity ?? "");
      group.vehicles.push(`${identity.vehicleSpec ?? name} ${vehicleQty}×${price}`); busGroups.set(snapshot.groupId, group);
    } else if (context.category === "misc" && snapshot.quantityMode === "quantity") {
      records.push(`${name}${role ? ` ${role}` : ""} ${formatIntegerFactor(line.quantity) ?? line.quantity}项×${price}`);
    } else records.push(`${name}${role ? ` ${role}` : ""} ${formula}`);
  }
  busGroups.forEach(group => records.push(`${group.header} ${group.vehicles.join(",")}`));
  if (legacyBus.length) records.push(...formatLegacyBusManualEntries(legacyBus));
  return records.join("\n");
}
/** 将新版人工明细回读为可编辑文本。 */
export function formatManualEntries(lines: QuoteBudgetLine[], context: ManualEntryContext): string {
  return formatManualEntriesText(lines, context);
}
/** 只兼容与当前明细旧回填全文一致的大巴草稿，人工编辑和不完整范围保持原文。 */
export function normalizeBusDrawerDraft(draft: QuoteBudgetDrawerDraft, lines: QuoteBudgetLine[]): QuoteBudgetDrawerDraft {
  if (draft.category !== "bus" || draft.textEdited || !draft.replaceIds.length
    || new Set(draft.replaceIds).size !== draft.replaceIds.length) return draft;
  const byId = new Map(lines.map(line => [line.budgetLineId, line]));
  const replaced = draft.replaceIds.map(id => byId.get(id));
  if (!replaced.every((line): line is QuoteBudgetLine => line !== undefined
    && ["包车", "大巴车"].includes(line.resourceType) && line.entrySnapshot?.inputMode === "manual_v2"
    && Boolean(line.entrySnapshot.context?.dayNumbers?.length))) return draft;
  // 旧缓存可能仍写「包车」，与现行「当地用车」回填等价，需一并识别后规范化。
  const expanded = formatManualEntriesText(replaced, draft, true);
  const expandedLegacy = expanded.replaceAll("当地用车", "包车");
  if (draft.text !== expanded && draft.text !== expandedLegacy) return draft;
  const text = formatManualEntriesText(replaced, draft);
  return text === draft.text ? draft : { ...draft, text };
}
/** 解析新版单行公式，旧行由原有入口处理。 */
export function parseManualLineFormula(text: string, line: QuoteBudgetLine, context: ManualEntryContext): ManualEntryParseResult {
  const source: ManualEntrySource = { text, start: 0, end: text.length, line: 1 };
  const item = checked(source, () => {
    const snapshot = line.entrySnapshot;
    if (snapshot?.inputMode !== "manual_v2") throw new EntryFailure("invalid", "legacy_line", "旧明细请使用原公式入口");
    const parts = formulaParts(text), periodic = /^人[晚餐次天]$/u.test(line.unit);
    const periodUnit = line.unit === "人次" ? "次场" : line.unit.slice(1);
    validateUnits(text, parts.length === 1 ? ["元"] : snapshot.quantityMode === "quantity" ? [line.unit, "元"] : periodic ? parts.length === 3 ? ["人", periodUnit, "元"] : [periodUnit, "元"] : ["人", "元"]);
    let mode = snapshot.quantityMode, count: number | null = mode === "fixed_people" ? Number(line.manualPeopleCount) : mode === "quantity" ? Number(line.quantity) : null;
    let factor = snapshot.billingCount, price: string | null;
    if (parts.length === 1) price = readPrice(parts[0]);
    else if (mode === "total") throw new EntryFailure("invalid", "total_formula", "整项费用只填写金额");
    else if (mode === "quantity") {
      if (parts.length !== 2) throw new EntryFailure("invalid", "quantity_formula", "请填写数量×单价");
      count = requireFactor(parts[0], "数量"); factor = 1; price = readPrice(parts[1]);
    } else {
      const fullLength = periodic ? 3 : 2;
      if (parts.length !== fullLength && !(periodic && parts.length === 2)) throw new EntryFailure("invalid", "formula_invalid", "公式因素数量不正确");
      const explicit = parts.length === fullLength;
      if (explicit) {
        const rolePeople = roleCount(line.travelerRole ?? null, context);
        const numericCount = /^\d+$/u.test(parts[0]) ? Number(parts[0]) : Number.NaN;
        const unchangedLinked = Number.isSafeInteger(numericCount) && numericCount === rolePeople
          && (mode === "linked_people" || (context.category === "meal" && line.travelerRole === "学生"));
        const explicitCount = unchangedLinked ? numericCount : requireFactor(parts[0], "人数", context.category === "meal");
        if (context.category === "meal" && line.travelerRole === "学生" && explicitCount !== rolePeople) {
          throw new EntryFailure("invalid", "student_count_mismatch", `学生统一为${rolePeople}人，请省略人数或填写统一学生人数`);
        }
        count = unchangedLinked ? null : explicitCount; mode = unchangedLinked ? "linked_people" : "fixed_people";
      } else { mode = "linked_people"; count = null; }
      factor = periodic ? requireFactor(parts.at(-2)!, line.unit === "人晚" ? "晚数" : line.unit === "人餐" ? "餐数" : line.unit === "人天" ? "天数" : "次数") : 1;
      price = readPrice(parts.at(-1)!);
    }
    if (parts.length === 1 && !periodic && mode === "fixed_people" && line.travelerRole) { mode = "linked_people"; count = null; }
    if (count !== null && (!Number.isSafeInteger(count) || count < (context.category === "meal" ? 0 : 1))) throw new EntryFailure("invalid", "people_invalid", `固定人数或数量必须为${context.category === "meal" ? "非负" : "正"}整数`);
    return [makeDraft({ name: line.itemNameSnapshot ?? line.skuNameSnapshot ?? "", resourceEntryType: line.resourceEntryType, role: line.travelerRole, unit: line.unit, mode, count, factor, price,
      group: snapshot.groupId, label: snapshot.groupLabel, identity: snapshot.context, mealType: line.mealType ?? undefined, spuId: line.spuId ?? undefined, skuId: line.skuId ?? undefined,
    }, { ...context, groupId: snapshot.groupId })];
  });
  return finish([item], context);
}
